import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import { resolveCourseIdForTeacher } from '../utils/batchCourse';
import { normalizeClassTimeForInput } from '../utils/classTimeUtils';
import PageTemplate from './PageTemplate';
import './ModulePage.css';

function EditBatch() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    batch_name: '',
    batch_type: 'weekend',
    teacher: '',
    start_date: '',
    end_date: '',
    class_time: '',
    capacity: 10,
    status: 'upcoming',
    student: '',
    live_kit: '',
  });

  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [batchCourseId, setBatchCourseId] = useState('');
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load courses and teachers for dropdowns
  useEffect(() => {
    async function loadData() {
      try {
        // Load courses
        const coursesRes = await apiClient.get('/api/courses/');
        const coursesList = Array.isArray(coursesRes.data)
          ? coursesRes.data
          : coursesRes.data?.courses?.results || coursesRes.data?.courses || [];
        setCourses(coursesList);

        // Load teachers
        const teachersRes = await apiClient.get('/api/auth/teachers/');
        const teachersList = Array.isArray(teachersRes.data)
          ? teachersRes.data
          : teachersRes.data?.teachers?.results || teachersRes.data?.teachers || [];
        setTeachers(teachersList);
      } catch (err) {
        console.error('Error loading courses/teachers:', err);
        setError('Unable to load courses or teachers. Please refresh the page.');
      } finally {
        setLoadingTeachers(false);
      }
    }

    loadData();
  }, []);

  // Load existing batch data
  useEffect(() => {
    async function loadBatch() {
      setLoading(true);
      setError('');
      try {
        const res = await apiClient.get(`/api/batches/${id}/`);   
        // Backend returns: { "message": "...", "batch": {...} }
        const batch = res.data?.batch || res.data;

        const courseIdRaw =
          batch.course?.course_id ?? batch.course?.id ?? batch.course ?? batch.course_id;
        setBatchCourseId(courseIdRaw != null ? String(courseIdRaw) : '');

        const teacherIdRaw =
          batch.teacher?.teacher_id ??
          batch.teacher?.id ??
          batch.teacher_id ??
          batch.teacher;
        const teacherId =
          teacherIdRaw != null && typeof teacherIdRaw !== 'object'
            ? String(teacherIdRaw)
            : '';

        setForm({
          batch_name: batch.batch_name || batch.name || '',
          batch_type: batch.batch_type || batch.type || 'weekend',
          teacher: teacherId,
          start_date: batch.start_date || batch.startDate || '',
          end_date: batch.end_date || batch.endDate || '',
          class_time: normalizeClassTimeForInput(
            batch.class_time ?? batch.startTime ?? ''
          ),
          capacity: batch.capacity || 10,
          status: batch.status || 'upcoming',
          student: batch.student || '',
          live_kit: batch.live_kit || '',
        });
      } catch (e) {
        console.error('Error loading batch:', e);
        if (e?.response?.status === 404) {
          setError('Batch not found.');
          setTimeout(() => navigate('/batches'), 2000);
        } else if (e?.response?.status === 401) {
          navigate('/login', { replace: true });
        } else {
          setError(
            e?.response?.data?.detail ||
              e?.response?.data?.message ||
              'Unable to load batch. Please try again.'
          );
        }
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      loadBatch();
    }
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const getTeacherDisplayName = (teacher) => {
    const teacherName = teacher?.full_name || teacher?.name || 'Unknown teacher';
    const directCourseName =
      teacher?.course_name ||
      teacher?.assigned_course_name ||
      teacher?.course?.course_name ||
      teacher?.course?.name ||
      '';

    if (directCourseName) {
      return `${teacherName} (${directCourseName})`;
    }

    const teacherCourseId =
      teacher?.course_id || teacher?.assigned_course || teacher?.course?.course_id || teacher?.course;
    const matchedCourse = courses.find((course) => {
      const courseId = course?.course_id || course?.id;
      return String(courseId || '') === String(teacherCourseId || '');
    });

    const fallbackCourseName = matchedCourse?.course_name || matchedCourse?.name || '';
    return fallbackCourseName ? `${teacherName} (${fallbackCourseName})` : teacherName;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmedName = form.batch_name.trim();

    if (
      !trimmedName ||
      !form.teacher ||
      !form.start_date ||
      !form.end_date ||
      !form.class_time
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    const capacityNumber = Number(form.capacity) || 10;

    const teacherId = String(form.teacher).trim();
    const teacher = teachers.find(
      (t) => String(t.teacher_id || t.id) === teacherId
    );
    const courseId =
      resolveCourseIdForTeacher(teacher, courses) || batchCourseId;
    if (!courseId) {
      setError(
        'The selected teacher has no course assigned. Please assign a course to the teacher first.'
      );
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        batch_name: trimmedName,
        batch_type: form.batch_type,
        course: courseId,
        teacher: teacherId,
        start_date: form.start_date,
        end_date: form.end_date,
        class_time: form.class_time,
        capacity: capacityNumber,
        status: form.status || 'upcoming',
        student: form.student.trim() || '',
        live_kit: form.live_kit.trim() || '',
      };

      await apiClient.put(`/api/batches/${id}/`, payload);

      setSuccess('Batch updated successfully.');
      setTimeout(() => navigate('/batches'), 600);
    } catch (err) {
      console.error('Batch update error:', err);
      const errorMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        Object.values(err?.response?.data || {})
          .flat()
          .find((msg) => typeof msg === 'string') ||
        err?.message ||
        'Unable to update batch. Please try again.';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageTemplate title="Edit Batch" subtitle="Update batch details">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading batch details...</p>
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title="Edit Batch" subtitle="Update batch details">
      <div className="add-teacher-panel">
        <div className="add-teacher-header">
          <div>
            <h2>Batch details</h2>
            <p>
              Update the batch type, schedule, and capacity. Maximum 10 students per batch.
            </p>
          </div>
          <span className="add-teacher-badge">Weekday &amp; Weekend batches</span>
        </div>

        <form className="add-teacher-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="batch_name">Batch Name</label>
            <input
              id="batch_name"
              name="batch_name"
              type="text"
              placeholder="e.g. Batch A"
              value={form.batch_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="batch_type">Batch Type</label>
            <select
              id="batch_type"
              name="batch_type"
              value={form.batch_type}
              onChange={handleChange}
              className="select-input"
              required   
            >
              <option value="weekend">Weekend (Sat–Sun)</option>
              <option value="weekdays">Weekdays (Mon–Fri)</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="teacher">Teacher</label>
            <select
              id="teacher"
              name="teacher"
              value={form.teacher}
              onChange={handleChange}
              className="select-input"
              required
              disabled={loadingTeachers}
            >
              <option value="">Select a teacher</option>
              {teachers.map((teacher) => (
                <option
                  key={teacher.teacher_id || teacher.id}
                  value={teacher.teacher_id || teacher.id}
                >
                  {getTeacherDisplayName(teacher)}
                </option>
              ))}
            </select>
            {loadingTeachers && (
              <small style={{ color: '#6b7280' }}>Loading teachers...</small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="start_date">Start Date</label>
            <input
              id="start_date"
              name="start_date"
              type="date"
              value={form.start_date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="end_date">End Date</label>
            <input
              id="end_date"
              name="end_date"
              type="date"
              value={form.end_date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="class_time">Class Time</label>
            <input
              id="class_time"
              name="class_time"
              type="time"
              step="60"
              value={form.class_time}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="live_kit">LiveKit room / link (optional)</label>
            <input
              id="live_kit"
              name="live_kit"
              type="text"
              placeholder="e.g. room name or session URL"
              value={form.live_kit}
              onChange={handleChange}
            />
            <small style={{ color: '#6b7280' }}>Optional: used for live class integration</small>
          </div>

          {error && <p className="form-error">{error}</p>}
          {success && <p className="form-success">{success}</p>}

          <div className="add-teacher-actions">
            <button
              type="button"
              className="btn-primary btn-secondary"
              onClick={() => navigate('/batches')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={submitting}
            >
              {submitting ? 'Updating…' : 'Update Batch'}
            </button>
          </div>
        </form>
      </div>
    </PageTemplate>
  );
}
export default EditBatch;


   